type IdLike = number | string | null | undefined;

export type ProposalRenewalAddressLike = {
  id?: IdLike;
  title?: string | null;
  state?: string | null;
  city?: string | null;
};

export type ProposalRenewalCompanyLike = {
  id?: IdLike;
};

export type ProposalRenewalSourceLike = {
  company?: ProposalRenewalCompanyLike | null;
  address?: ProposalRenewalAddressLike | null;
};

export type ProposalRenewalAttributeLike = {
  attributeId?: IdLike;
  quantity?: number | string | null;
  period?: number | string | null;
  [key: string]: any;
};

const hasValue = (value: IdLike): value is number | string =>
  value !== null && value !== undefined && String(value).trim() !== '';

const normalizeText = (value?: string | null): string =>
  (value ?? '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr-TR');

const toPositiveInteger = (value: string): number | null => {
  const parsedValue = Number(value);

  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const toFiniteNumber = (value: unknown, fallback: number): number => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const parseRenewalProposalIds = (search: string): number[] => {
  const params = new URLSearchParams(search);
  const rawValues: string[] = [];

  params.getAll('proposalIds').forEach((value) => {
    rawValues.push(...value.split(','));
  });
  params.getAll('proposalId').forEach((value) => {
    rawValues.push(...value.split(','));
  });

  const seen = new Set<number>();
  const proposalIds: number[] = [];

  rawValues.forEach((value) => {
    const proposalId = toPositiveInteger(value.trim());

    if (proposalId !== null && !seen.has(proposalId)) {
      seen.add(proposalId);
      proposalIds.push(proposalId);
    }
  });

  return proposalIds;
};

export const getProposalRenewalGroupKey = (
  proposal: ProposalRenewalSourceLike | null | undefined
): string | null => {
  const companyId = proposal?.company?.id;

  if (!hasValue(companyId)) {
    return null;
  }

  const address = proposal?.address;
  const addressId = address?.id;

  if (hasValue(addressId)) {
    return `company:${companyId}|address:${addressId}`;
  }

  const title = normalizeText(address?.title);
  const state = normalizeText(address?.state);
  const city = normalizeText(address?.city);

  if (!title && !state && !city) {
    return null;
  }

  return `company:${companyId}|location:${title}|${state}|${city}`;
};

export const areProposalRenewalSourcesCompatible = (
  proposals: ProposalRenewalSourceLike[]
): boolean => {
  if (proposals.length <= 1) {
    return true;
  }

  const firstGroupKey = getProposalRenewalGroupKey(proposals[0]);

  return Boolean(firstGroupKey)
    && proposals.every((proposal) => getProposalRenewalGroupKey(proposal) === firstGroupKey);
};

export const mergeRenewalProposalAttributes = (
  attributeGroups: Array<Array<ProposalRenewalAttributeLike> | null | undefined>
): ProposalRenewalAttributeLike[] => {
  const mergedAttributes = new Map<string, ProposalRenewalAttributeLike>();

  attributeGroups.forEach((attributes) => {
    (attributes ?? []).forEach((attribute) => {
      const attributeId = attribute?.attributeId;

      if (!hasValue(attributeId)) {
        return;
      }

      const key = String(attributeId);
      const quantity = toFiniteNumber(attribute.quantity, 0);
      const period = toFiniteNumber(attribute.period, 1);
      const existingAttribute = mergedAttributes.get(key);

      if (!existingAttribute) {
        mergedAttributes.set(key, {
          ...attribute,
          quantity,
          period,
        });
        return;
      }

      existingAttribute.quantity = toFiniteNumber(existingAttribute.quantity, 0) + quantity;
      existingAttribute.period = Math.max(toFiniteNumber(existingAttribute.period, 1), period);
    });
  });

  return Array.from(mergedAttributes.values());
};

export const mergeAuthorizedPersonIds = (
  authorizedPersonIdGroups: Array<Array<number | string> | null | undefined>
): number[] => {
  const seen = new Set<number>();
  const authorizedPersonIds: number[] = [];

  authorizedPersonIdGroups.forEach((ids) => {
    (ids ?? []).forEach((id) => {
      const parsedId = Number(id);

      if (Number.isInteger(parsedId) && parsedId > 0 && !seen.has(parsedId)) {
        seen.add(parsedId);
        authorizedPersonIds.push(parsedId);
      }
    });
  });

  return authorizedPersonIds;
};
